import { useLayoutEffect } from 'react';
import { Platform } from 'react-native';

/**
 * Moves keyboard / VoiceOver focus into the topmost open action-sheet dialog.
 * Uses `data-fhs-action-sheet-dialog` first, then `[aria-modal="true"]`, because RN Web / Reanimated
 * may not forward every ARIA prop to the same DOM node.
 *
 * Runs in `useLayoutEffect` + rAF + delayed retries so focus wins races with portal mount and
 * `useSuppressWebRootAccessibility` (inert / aria-hidden on the page).
 *
 * If the dialog container does not take focus (Safari + RN Web), falls back to the first
 * focusable control inside the dialog (typically Dismiss).
 */
export function useFocusWebAriaModalOnOpen(isActive: boolean) {
  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || !isActive || typeof document === 'undefined') {
      return;
    }

    let cancelled = false;
    let raf1 = 0;
    let raf2 = 0;

    const resolveDialogEl = (): HTMLElement | null => {
      const marked = Array.from(
        document.querySelectorAll('[data-fhs-action-sheet-dialog]')
      );
      const fromData = marked[marked.length - 1];
      if (fromData instanceof HTMLElement) {
        return fromData;
      }
      const list = Array.from(document.querySelectorAll('[aria-modal="true"]'));
      const fromAria = list[list.length - 1];
      return fromAria instanceof HTMLElement ? fromAria : null;
    };

    const focusFirstInside = (dialog: HTMLElement) => {
      const preferHeading = dialog.querySelector('[data-fhs-alert-dialog-heading]');
      if (preferHeading instanceof HTMLElement) {
        preferHeading.focus({ preventScroll: true });
        if (document.activeElement === preferHeading) {
          return;
        }
      }
      const preferDismiss = dialog.querySelector('[data-fhs-action-sheet-dismiss]');
      if (preferDismiss instanceof HTMLElement) {
        preferDismiss.focus({ preventScroll: true });
        return;
      }
      const first = dialog.querySelector(
        'button:not([disabled]), [role="button"]:not([aria-disabled="true"]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (first instanceof HTMLElement) {
        first.focus({ preventScroll: true });
      }
    };

    const focusTopModal = () => {
      if (cancelled) {
        return;
      }
      const el = resolveDialogEl();
      if (!el) {
        return;
      }

      const active = document.activeElement;
      /**
       * If focus is already inside this dialog (dialog root, a sheet heading, input, etc.), do not
       * move it back to the dialog container — that re-announces aria-label and steals intentional
       * focus (e.g. promo drawer focusing the title+intro block).
       */
      if (active instanceof Node && el.contains(active)) {
        return;
      }

      el.setAttribute('tabindex', '-1');
      el.focus({ preventScroll: true });

      const afterActive = document.activeElement;
      if (!el.contains(afterActive)) {
        focusFirstInside(el);
      }
    };

    focusTopModal();
    raf1 = requestAnimationFrame(() => {
      focusTopModal();
      raf2 = requestAnimationFrame(() => focusTopModal());
    });

    const t0 = window.setTimeout(focusTopModal, 0);
    const t1 = window.setTimeout(focusTopModal, 50);
    const t2 = window.setTimeout(focusTopModal, 150);
    const t3 = window.setTimeout(focusTopModal, 350);
    const t4 = window.setTimeout(focusTopModal, 600);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(t0);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.clearTimeout(t4);
    };
  }, [isActive]);
}
